// EditableField — convenience wrapper around <EditableText> that handles
// the IDB persistence boilerplate (override resolve / commit / revert) given
// the course context.
//
// Per陛下 19AQ: "用户编辑功能应该给 essay 讲义 推导 这些模块都加上 包括
// flashcard". Without this helper every view would repeat ~30 lines of
// resolveField + updateProgress / refreshProgress / overrideKey wiring; this
// component encapsulates it so adding a new editable field is a 1-liner.
//
// Per LLM_NATIVE_ARCHITECTURE.md §2.1: every AI-generated field carries a
// `data-source` provenance attribute.  Value is currently 'ai-generated';
// future enhancement may expose a `source` prop for finer granularity
// (e.g. 'ai-deepseek-v4-pro' / 'user-edited').

import type { ReactNode } from 'react';
import { resolvedField, overrideKey } from '@examforge/shared/schemas';
// Isomorphism Phase 3: Zustand primary (replacing Context).
import { useStoreCourseData } from '../lib/hooks/use-store-course.js';
import { updateProgress } from '../storage/idb.js';
import { EditableText } from './editable-text.js';
import { FlagButton } from './flag-button.js';

export interface EditableFieldProps {
  /** Item id (mcq.id / flashcard.id / essay.id / lecture.id / proof.id). */
  itemId: string;
  /** Item kind tag for ItemOverride.itemKind ('mcq' / 'flashcard' / etc). */
  itemKind: string;
  /** Field name on the item (e.g. 'explanation' / 'back' / 'modelAnswer'). */
  field: string;
  /** AI-original text to fall back to. */
  aiOriginal: string;
  /** Optional UI label overrides forwarded to EditableText. */
  labels?: Parameters<typeof EditableText>[0]['labels'];
  /** When true, textarea opens with larger row count (essay / lecture). */
  large?: boolean;
  /** Optional additional content below the editor (e.g. badge row). */
  trailing?: ReactNode;
}

export function EditableField({
  itemId, itemKind, field, aiOriginal, labels, large, trailing,
}: EditableFieldProps) {
  const { draft, progress, refreshProgress } = useStoreCourseData();
  const resolved = resolvedField(progress.itemOverrides, itemId, field, aiOriginal);

  async function commit(newText: string): Promise<void> {
    await updateProgress(draft.id, (p) => {
      if (!p.itemOverrides) p.itemOverrides = {};
      p.itemOverrides[overrideKey(itemId, field)] = {
        itemKind,
        field,
        editedText: newText,
        editedAt: new Date().toISOString(),
        originalSnapshot: resolved.original,
      };
    });
    await refreshProgress();
  }

  async function revert(): Promise<void> {
    await updateProgress(draft.id, (p) => {
      if (p.itemOverrides) {
        delete p.itemOverrides[overrideKey(itemId, field)];
      }
    });
    await refreshProgress();
  }

  return (
    <div className="editable-field-wrap" data-source="ai-generated">
      <EditableText
        text={resolved.text}
        isEdited={resolved.isEdited}
        onCommit={commit}
        onRevert={revert}
        {...(labels !== undefined ? { labels } : {})}
        {...(large !== undefined ? { large } : {})}
      />
      <div className="editable-field-aside">
        {/* 19AQ · F5: every editable AI field also gets a flag/rate button. */}
        <FlagButton itemId={itemId} field={field} />
      </div>
      {trailing}
    </div>
  );
}
